/**
 * vc-signals.types — CONTRATO COMPARTIDO de detección de videoconferencia (VC).
 *
 * Archivo canónico (ver CONTRATO-VC-DETECTION.md §1 y §5). Todo lo que vive
 * acá es dato puro — sin lógica — para que watchers, `VcScorerService`, el
 * gate pre-start y el agente FC programen contra la MISMA forma. Si necesitás
 * cambiar un peso, un half-life o un patrón de device virtual, este es el
 * único lugar: no lo dupliques ni lo hardcodees en otro archivo.
 */

/** Identificador único de cada señal que puede alimentar el score de VC. */
export type VcSignalId =
  | 'virtual-device-present'      // device virtual instalado (snapshot en gate)
  | 'virtual-device-appeared'     // apareció un device virtual mid-test (devicechange)
  | 'default-output-virtual'      // el default output conmutó a un device virtual
  | 'active-track-virtual'        // NUESTRO stream viene de un device virtual
  | 'remote-session-device'       // "Remote Audio" / "Citrix HDX" → RDP/VDI
  | 'device-contention'           // NotReadableError / track mute|ended inesperado
  | 'second-voice'                // voz sostenida con labios cerrados
  | 'second-person'               // 2+ caras en la webcam
  | 'localhost-probe-hit'         // Discord/RAT respondió en loopback
  | 'faces-on-screen'             // caras detectadas en la PANTALLA COMPARTIDA
  | 'screen-share-stopped'        // el candidato cortó el screen share
  | 'screen-share-partial'        // compartió ventana/pestaña en vez del monitor
  | 'multi-monitor'               // ya existe, se reusa
  | 'read-speech'                 // habla leída (repregunta/respuesta técnica vs saludo espontáneo) — ver read-speech.service.ts
  // -- Señales ADVISORY (SPEC-INTEGRITY-SIGNALS) --------------------------
  // Hechos de COMPORTAMIENTO que hoy solo se avisan al candidato (LA REGLA DE
  // ORO de fc-session.component.ts: "CONTAR siempre, AVISAR con cooldown,
  // PUNTUAR nunca"). Pasan a alimentar el eje de INTEGRIDAD vía
  // IntegritySignalsService — nunca el `score` de enforcement (ver
  // `VcSignalSpec.advisory`). Traducidas 1:1 desde los servicios que ya
  // miden el hecho — este archivo no inventa umbrales nuevos.
  | 'no-face-sustained'           // rostro ausente sostenido — umbral de 15s de SustainedNoFaceService (BANNER_MS)
  | 'gaze-off-sustained'          // mirada fuera de pantalla sostenida — duty-cycle de VisionEpisodeTracker (GAZE_DUTY_ALERT_MS/GAZE_WINDOW_MS)
  | 'tab-switch-burst'            // ráfaga de cambios de pestaña/ventana en ventana rodante corta — ver IntegritySignalsService.tabSwitch()
  | 'fullscreen-exit'             // salida de pantalla completa
  | 'voice-mismatch';             // voz sostenida con F0 fuera del rango del candidato — ver voice-identity.service.ts

/** Familia a la que pertenece una señal — usada para el bonus de corroboración cruzada. */
export type VcSignalFamily = 'device' | 'audio' | 'vision' | 'screen' | 'system';

/** Lo que reporta un watcher/servicio al detectar una señal puntual. */
export interface VcSignalEvent {
  id: VcSignalId;
  at: number;                       // Date.now()
  confidence: number;               // 0..1
  detail: Record<string, unknown>;  // evidencia auditable (label matcheado, rms, jawOpen, etc.)
}

/**
 * Parámetros de scoring de una señal — ver tabla `VC_SIGNAL_SPECS` más abajo.
 *
 * `advisory` — GARANTÍA DE SEGURIDAD de todo SPEC-INTEGRITY-SIGNALS, léela
 * dos veces antes de tocarla:
 *
 *   `advisory: true`  → la señal suma SOLO al `integrityScore`/`integrityLevel`
 *                        (eje informativo para el evaluador). NUNCA participa
 *                        del `score` que gobierna `applyHysteresis`, los
 *                        niveles (`watch`/`suspect`/`confirmed`), el atajo de
 *                        confirmación directa ni ningún enforcement (avisos
 *                        de suspensión/corte de prueba). Da igual cuán alto
 *                        sea su `weight` o cuántas veces se reporte con
 *                        confidence 1.0 — no puede, por construcción, mover
 *                        `VcScorerService.level` fuera de `'clear'`.
 *   `undefined`/`false` → comportamiento de SIEMPRE: participa del `score` de
 *                        enforcement exactamente como antes de este campo.
 *
 * Por qué existe: la decisión de producto (2026-09-29, ver mem_save) es que
 * las señales de COMPORTAMIENTO (mirada, rostro ausente, segunda voz, tab
 * switching) todavía no tienen umbrales calibrados — un candidato honesto ya
 * midió duty-cycle de mirada 25.7%/35%/26.3% contra una alerta que en ese
 * momento estaba en 40%, margen real ≈ 0 (el umbral se movió después en
 * respuesta a este mismo dato — ver vision-thresholds.ts — pero eso no
 * cambia la conclusión: comportamiento sin calibrar no debe poder cortar).
 * Confundir "informa al evaluador" con "puede suspender" en ese
 * terreno sin calibrar produciría falsos positivos de alto impacto. Solo las
 * señales de DEVICE/SCREEN/system "duras" (weight alto, `advisory` ausente)
 * conservan poder de enforcement — su semántica y umbrales de hoy quedan
 * INTACTOS.
 *
 * NO le saques `advisory` a una señal sin revisar el contrato Y sin datos de
 * calibración que lo justifiquen — ver el describe dedicado en
 * `vc-scorer.service.spec.ts` ("garantía de seguridad: advisory nunca puede
 * suspender") que existe específicamente para bloquear ese error en el futuro.
 */
export interface VcSignalSpec {
  id: VcSignalId;
  weight: number;
  halfLifeMs: number;               // Number.POSITIVE_INFINITY = no decae
  cooldownMs: number;               // Number.POSITIVE_INFINITY = aporta una sola vez
  family: VcSignalFamily;
  advisory?: boolean;                // ver docblock de la interfaz — GARANTÍA DE SEGURIDAD
}

export type VcLevel = 'clear' | 'watch' | 'suspect' | 'confirmed';

/** Snapshot reactivo que expone `VcScorerService` (ver `state`/`state$`). */
export interface VcScoreState {
  score: number;
  level: VcLevel;
  families: VcSignalFamily[];       // familias con aporte > 0 en la ventana actual (SOLO no-advisory)
  events: VcSignalEvent[];          // ring buffer, máx 200
  /**
   * Aditivo (SPEC-INTEGRITY-SIGNALS) — eje DISJUNTO de `score`: suma
   * EXCLUSIVAMENTE las señales `advisory: true`, y sin el bonus ×1.3 de
   * familias que sí lleva `score` (ver `computeScore` para el porqué de las
   * dos cosas). Ver `VcSignalSpec.advisory` para la garantía de que esto
   * NUNCA retroalimenta `score`/`level`.
   */
  integrityScore: number;
  /** Familias con aporte > 0 en la ventana actual, SOLO advisory — espejo de
   *  `families` para el otro eje. Dato para el evaluador ("¿de cuántos
   *  frentes distintos vino esto?"); NO multiplica el score. */
  integrityFamilies: VcSignalFamily[];
  /**
   * Aditivo — rótulo derivado de `integrityScore` con la MISMA escala que
   * `VcLevel` pero SIN histéresis ni stickiness (se recalcula del número
   * puro en cada tick, puede subir y bajar libremente). Puramente
   * informativo — nunca dispara enforcement, nunca toca `level`.
   */
  integrityLevel: VcLevel;
}

/**
 * Tabla de pesos (EXACTA — no improvisar). Fórmula de score (ver
 * CONTRATO-VC-DETECTION.md §1):
 *
 *   aporte(s) = weight(s) × confidence(último evento de s) × 2^(-(now - lastAt(s)) / halfLife(s))
 *   score     = Σ aportes (SOLO señales con advisory !== true)
 *   si ≥2 familias distintas aportan > 0 dentro de una ventana de 120s → score × 1.3
 *
 * `integrityScore` usa la MISMA fórmula de aporte por señal, pero corre
 * sobre un conjunto DISJUNTO: Σ aportes SOLO de señales `advisory: true`, y
 * SIN el bonus ×1.3 de familias — ese bonus es EXCLUSIVO del eje de
 * enforcement (`score`, ver `computeScore()` en vc-scorer.service.ts para el
 * porqué: las señales advisory están correlacionadas por causa común —p. ej.
 * una webcam mala dispara `second-voice` (audio), `no-face-sustained` y
 * `gaze-off-sustained` (vision) ella sola—, así que premiar "≥2 familias" en
 * ese eje inflaría evidencia que en realidad viene de una sola fuente
 * física). `integrityScore` es SUMA PELADA. Ver `VcSignalSpec.advisory` para
 * la garantía de que esto nunca corre en sentido inverso (integrityScore no
 * puede alimentar el score de enforcement, ni sus señales pueden colarse en
 * `families`).
 *
 * Un evento del mismo `id` dentro de su `cooldownMs` refresca `lastAt` pero
 * no suma de nuevo (ver `VcScorerService.report()`).
 */
export const VC_SIGNAL_SPECS: readonly VcSignalSpec[] = [
  { id: 'active-track-virtual',    weight: 45, halfLifeMs: Infinity, cooldownMs: 60_000,  family: 'device' },
  { id: 'default-output-virtual',  weight: 40, halfLifeMs: 300_000,  cooldownMs: 60_000,  family: 'device' },
  { id: 'remote-session-device',   weight: 40, halfLifeMs: Infinity, cooldownMs: 60_000,  family: 'device' },
  { id: 'faces-on-screen',         weight: 40, halfLifeMs: 180_000,  cooldownMs: 30_000,  family: 'screen' },
  { id: 'virtual-device-appeared', weight: 30, halfLifeMs: 300_000,  cooldownMs: 30_000,  family: 'device' },
  { id: 'localhost-probe-hit',     weight: 30, halfLifeMs: 600_000,  cooldownMs: 120_000, family: 'system' },
  { id: 'screen-share-stopped',    weight: 30, halfLifeMs: 300_000,  cooldownMs: 60_000,  family: 'screen' },
  // weight 0 = NEUTRALIZADA a proposito. Sigue reportando y logueando para
  // calibrar, pero NO suma al score ni puede suspender a nadie.
  //
  // Por que: en la prueba en dev del 2026-09-28 disparo CUATRO veces contra un
  // candidato honesto mientras respondia en voz alta. El ASR transcribia
  // "es que trabajaria el tema de transiciones de base de datos" y jawOpen
  // llegaba en 0.0007-0.013 — mandibula practicamente cerrada. La medicion de
  // apertura de boca no seguia la cara real (distancia/angulo/luz), asi que la
  // premisa "voz + boca cerrada = otra persona" se rompe.
  //
  // Con weight 25 y confidence 0.7 aportaba 17.5, a 2.5 puntos del umbral de
  // 'watch'. Una señal mas y cruzaba, contra alguien que no hizo nada.
  //
  // Para reactivarla hace falta: validar que jawOpen sube cuando el candidato
  // habla (loguear la serie completa, no solo el momento del disparo), y
  // probablemente cambiar el criterio de valor absoluto a VARIANZA — una boca
  // que habla oscila, una cerrada queda plana, y la varianza no depende de
  // cuan lejos este la cara de la camara.
  //
  // REACTIVACION (SPEC-INTEGRITY-SIGNALS, 2026-09-29) — otro agente reescribió
  // second-voice.service.ts para medir el RECORRIDO de jawOpen (max−min sobre
  // la racha sostenida) en vez del valor absoluto instantáneo, más una
  // ventana de RMS agregada en vez de una lectura puntual: eso resuelve
  // exactamente el problema de arriba (una boca que habla oscila aunque la
  // cara esté lejos/mal iluminada; el recorrido lo capta, el valor absoluto
  // no). Con la medición corregida hay base para reactivarla, pero SOLO como
  // `advisory: true` — sigue siendo comportamiento sin calibrar en producción
  // (mismo principio que gaze/no-face más abajo), así que informa al
  // evaluador pero no puede volver a disparar los 4 falsos positivos del
  // 2026-09-28 contra nadie. weight bajo (10, la mitad de second-person)
  // porque la corroboración de audio+visión sigue siendo indirecta.
  { id: 'second-voice',            weight: 10, halfLifeMs: 180_000,  cooldownMs: 20_000,  family: 'audio', advisory: true },
  { id: 'second-person',           weight: 25, halfLifeMs: 180_000,  cooldownMs: 20_000,  family: 'vision' },
  { id: 'device-contention',       weight: 15, halfLifeMs: 300_000,  cooldownMs: 60_000,  family: 'device' },
  { id: 'screen-share-partial',    weight: 12, halfLifeMs: Infinity, cooldownMs: Infinity, family: 'screen' },
  { id: 'virtual-device-present',  weight: 10, halfLifeMs: Infinity, cooldownMs: Infinity, family: 'device' },
  { id: 'multi-monitor',           weight: 10, halfLifeMs: Infinity, cooldownMs: Infinity, family: 'system' },
  // read-speech (ver read-speech.service.ts) — habla leída vs espontánea
  // (corpus HESITA: 7.34 vs 0.22 muletillas/min, 33x). weight bajo A
  // PROPÓSITO: con weight 28 y confidence típica ~0.6-1.0, el aporte máximo
  // (~28) queda por debajo de SUSPECT_ENTER_SCORE=45 — esta señal SOLA
  // nunca suspende a nadie, necesita corroboración de otra familia (device/
  // vision/screen/system) para cruzar el umbral. Deliberado — NO subir el
  // weight sin revisar el contrato: es una señal de comportamiento/lenguaje,
  // más propensa a falsos positivos individuales (candidato formal, ASR que
  // suprime muletillas) que las señales de dispositivo/pantalla.
  { id: 'read-speech',             weight: 28, halfLifeMs: 300_000,  cooldownMs: 60_000,  family: 'audio'  },

  // ── Señales ADVISORY (SPEC-INTEGRITY-SIGNALS, 2026-09-29) ─────────────────
  // TODAS con `advisory: true` — ver el docblock de `VcSignalSpec.advisory`
  // para la garantía de seguridad completa. Resumen: suman a `integrityScore`
  // (eje informativo del evaluador), JAMÁS a `score` (enforcement). Los
  // weights de acá abajo son deliberadamente MÁS BAJOS que sus pares
  // no-advisory de la misma familia — no porque el hecho importe menos, sino
  // porque son señales de comportamiento sin calibrar: el número solo tiene
  // que ordenar razonablemente la severidad relativa dentro del eje
  // informativo, nunca cruzar un umbral de enforcement (no puede, por
  // construcción) así que no hace falta ni conviene inflarlos.

  // no-face-sustained: mismo umbral de 15s (BANNER_MS) que ya usa
  // SustainedNoFaceService para su banner disuasivo — no inventamos un
  // umbral nuevo, solo lo traducimos a señal de integridad. weight 20 (por
  // debajo de second-person=25): "no veo a nadie" es MENOS evidencia que
  // "veo a una segunda persona" — ausencia no es lo mismo que presencia de
  // otro. halfLife 180_000 — mismo número que second-person/second-voice,
  // misma familia (vision), misma cadencia de decaimiento esperable de un
  // episodio transitorio.
  //
  // cooldownMs 10_000 (CORRECCIÓN DEFECTO 1, revisión Opus 2026-09-29 — antes
  // 20_000). ACOPLAMIENTO DURO con `EPISODE_HEARTBEAT_MS = 15_000`
  // (vision-episode-tracker.ts): el consumidor (fc-session.component.ts)
  // llama `integritySignals.noFaceSustained(heartbeatElapsedMs)` en CADA
  // heartbeat, o sea cada 15s. `VcScorerService.report()` solo pisa
  // `confidence` cuando el evento llega FUERA del cooldown de su señal
  // (dentro del cooldown solo refresca `lastAt`, ver `report()` en
  // vc-scorer.service.ts) — con cooldown=20_000 > heartbeat=15_000, el
  // cooldown se refrescaba ANTES de vencer y NINGÚN heartbeat posterior al
  // primero volvía a actualizar la
  // confidence: la curva creciente de confidence de
  // `IntegritySignalsService.noFaceSustained` (0.5 a los 15s → 1.0 a los 45s)
  // quedaba muerta — ausentarse 15s o ausentarse 15 minutos valían
  // EXACTAMENTE lo mismo. `cooldownMs` de esta señal DEBE quedar
  // estrictamente por debajo de `EPISODE_HEARTBEAT_MS` para que esto no
  // vuelva a pasar en silencio — 10_000 deja ~5s (33%) de margen defensivo,
  // no una corrección de un jitter medido. SI TOCÁS UNA DE LAS DOS
  // CONSTANTES, TOCÁ LA OTRA — ver el mismo acoplamiento espejado en el
  // docblock de `EPISODE_HEARTBEAT_MS` en vision-episode-tracker.ts.
  { id: 'no-face-sustained',       weight: 20, halfLifeMs: 180_000,  cooldownMs: 10_000,  family: 'vision', advisory: true },

  // gaze-off-sustained: duty-cycle de VisionEpisodeTracker (proporción de
  // GAZE_DUTY_ALERT_MS/GAZE_WINDOW_MS — ver vision-thresholds.ts, umbral en
  // ajuste activo por otro agente durante esta misma fase, NO hardcodeamos
  // el % acá para no quedar desactualizados) o el atajo SUSTAINED_HARD_MS de
  // mirada HARD continua. Es LA señal que motivó este cambio completo: el
  // log real (2026-09-29) tiene un candidato honesto con duty 25.7%/35%/26.3%
  // contra una alerta que en ese momento estaba en 40% — margen real ≈ 0 (el
  // umbral ya se subió a 60% aguas arriba en respuesta a este mismo dato,
  // pero la razón de ser `advisory` no depende de DÓNDE quede el umbral: es
  // comportamiento sin calibrar en producción, punto). weight 15, el MÁS
  // BAJO de toda la tabla advisory, exactamente por eso: hasta que Fase B
  // calibre los umbrales HARD con datos reales, esta señal es la más
  // propensa a falso positivo de todas, y su aporte al eje informativo debe
  // reflejar esa incertidumbre incluso ahí.
  { id: 'gaze-off-sustained',      weight: 15, halfLifeMs: 180_000,  cooldownMs: 20_000,  family: 'vision', advisory: true },

  // tab-switch-burst: ver IntegritySignalsService.tabSwitch() para el N y la
  // ventana exactos (justificados ahí con el dato real: 7 tab_blur + 4
  // tab_focus en ~3min de un candidato honesto probando la app). weight 20,
  // halfLife y cooldown largos (300_000/60_000, igual que read-speech): es
  // una señal de PATRÓN acumulado en una ventana rodante, no un evento
  // instantáneo — no tiene sentido que decaiga ni se re-cuente tan rápido
  // como una señal puntual de visión.
  { id: 'tab-switch-burst',        weight: 20, halfLifeMs: 300_000,  cooldownMs: 60_000,  family: 'system', advisory: true },

  // fullscreen-exit: evento discreto (a diferencia de tab-switch-burst, no
  // hace falta acumular una ráfaga — salir de pantalla completa una vez ya es
  // el hecho completo). weight 12, igual que screen-share-partial (mismo
  // orden de magnitud que "el candidato cambió el modo de presentación de su
  // pantalla", no que "hay un dispositivo virtual"). halfLife/cooldown
  // 300_000/60_000 — no queremos que salir y volver a entrar en ráfaga
  // multiplique el aporte informativo sin agregar evidencia nueva.
  { id: 'fullscreen-exit',         weight: 12, halfLifeMs: 300_000,  cooldownMs: 60_000,  family: 'system', advisory: true },

  // voice-mismatch (ver voice-identity.service.ts, implementada por otro
  // agente) — voz sostenida con F0 fuera del rango del candidato. Es
  // potencialmente la señal de audio MÁS fuerte del lote advisory (F0 es un
  // rasgo biométrico más estable que rms/jawOpen), pero sigue siendo behavior
  // sin calibrar en producción — weight 22, por debajo de read-speech (28,
  // no-advisory) pero por encima del resto de advisory salvo no-face-
  // sustained. halfLife 300_000 (más largo que vision: un mismatch de F0 es
  // evidencia que no debería desvanecerse en 3 minutos como un desvío de
  // mirada) y cooldown 60_000 (evita floodear con re-detecciones del mismo
  // hablante sostenido).
  { id: 'voice-mismatch',          weight: 22, halfLifeMs: 300_000,  cooldownMs: 60_000,  family: 'audio', advisory: true },
];

/**
 * Labels de dispositivos virtuales (tabla canónica — CONTRATO-VC-DETECTION.md §5).
 *
 * `severity: 'hard'` presente y siendo la fuente activa → bloquea el gate pre-start.
 * `severity: 'soft'` presente pero no activo → solo score (`virtual-device-present`,
 * weight 10). NO bloquear por tener Krisp instalado — es falso positivo masivo.
 * `tool: 'remote-session'` presente → bloquea el gate siempre (`remote-session-device`).
 *
 * Los labels solo están poblados si hay permiso de cámara/mic activo. Si
 * `label === ''` para todos los devices → no se puede evaluar, no emitir nada
 * (NO asumir culpabilidad por labels vacíos).
 */
export const VIRTUAL_DEVICE_PATTERNS = [
  { regex: /zoomaudiodevice|zoom audio device/i,                 tool: 'zoom',             severity: 'soft' },
  { regex: /microsoft teams audio/i,                             tool: 'teams',            severity: 'soft' },
  { regex: /krisp (microphone|speaker)/i,                        tool: 'krisp',            severity: 'soft' },
  { regex: /vb-audio|cable (in|out)put|voicemeeter/i,            tool: 'vb-cable',         severity: 'hard' },
  { regex: /blackhole \d+ch|soundflower/i,                       tool: 'blackhole',        severity: 'hard' },
  { regex: /obs virtual|obs-camera/i,                            tool: 'obs',              severity: 'hard' },
  { regex: /manycam|snap camera|xsplit|mmhmm|splitcam|altercam|e2esoft/i, tool: 'vcam',     severity: 'hard' },
  { regex: /nvidia broadcast/i,                                  tool: 'nvidia-broadcast', severity: 'soft' },
  { regex: /droidcam|epoccam|iriun|reincubate camo/i,            tool: 'phone-cam',        severity: 'soft' },
  { regex: /^remote audio$|citrix hdx|vmware virtual|parallels audio/i, tool: 'remote-session', severity: 'hard' },
] as const;

/** Forma de una fila de `VIRTUAL_DEVICE_PATTERNS` — conveniencia derivada para
 *  quien necesite tipar un parámetro sin repetir la unión de `tool`/`severity`. */
export type VirtualDevicePattern = (typeof VIRTUAL_DEVICE_PATTERNS)[number];
